import { RelatorioDemonstrativoReceitaDespesaAnualPdfResponse } from "core/features/relatorios/typings";
import { PDFLayout } from "ui/components/PDF";
import SummaryItem from "components/PDF/components/SummaryItem/index";
import colors from "core/resources/theme/colors";
import { View, Text } from "@react-pdf/renderer";
import moment from "moment";
import { formatMoney } from "infra/helpers/number";
import {
  formatterDemonstrativoReceitaDespesaAnual,
  flattenRelatorioItems,
} from "./../RelatorioDemonstrativo/helper/util";
import OperacaoTable from "./components/OperacaoTable/index";

interface RelatorioDemonstrativoReceitaDespesaAnualPdfProps {
  data?: RelatorioDemonstrativoReceitaDespesaAnualPdfResponse & {
    ano?: string;
  };
  includeDocumentWrapper?: boolean;
}

function PDFRelatorioDemonstrativoReceitaDespesaAnual({
  data,
  includeDocumentWrapper = true,
}: RelatorioDemonstrativoReceitaDespesaAnualPdfProps) {
  const flattenReceitas = flattenRelatorioItems(data?.receitas || [], 0);
  const flattenDespesas = flattenRelatorioItems(data?.despesas || [], 0);

  const receitasTabela = flattenReceitas.map(
    formatterDemonstrativoReceitaDespesaAnual
  );
  const despesasTabela = flattenDespesas.map(
    formatterDemonstrativoReceitaDespesaAnual
  );

  return (
    <PDFLayout
      orientation="landscape"
      fontSize={7}
      includeDocument={includeDocumentWrapper}
      assinaturas={data?.assinaturas}
      headerProps={{
        logo: data?.logo!,
        tenant: data?.nomeTenant!,
        title: `Demonstrativo Receitas e Despesas Anual`,
        subTitle: data?.periodo,
        cards: [
          {
            title: "Total de Receitas",
            value: formatMoney(data?.totais.receita ?? 0),
          },
          {
            title: "Total de Despesas",
            value: formatMoney(data?.totais.despesa ?? 0),
          },
        ],
      }}
      footerProps={{
        tenant: data?.nomeTenant!,
        logo: data?.logoAdm!,
        endereco1: data?.endereco1!,
        endereco2: data?.endereco2!,
      }}
    >
      {data ? (
        <>
          <SummaryItem
            title={`Saldo em ${moment(`${data?.ano}-01-01`)
              .add(-1, "days")
              .format("DD/MM/YYYY")}`}
            value={data.totais.saldoInicial}
            bordered
          />

          <View style={{ marginTop: 6 }}>
            <OperacaoTable
              items={receitasTabela}
              tableTitle="Receitas"
              ano={data.ano || ""}
            />
          </View>

          <View style={{ marginTop: 12, marginBottom: 24 }}>
            <OperacaoTable
              items={despesasTabela}
              tableTitle="Despesas"
              ano={data.ano || ""}
            />
          </View>

          {data.transferencias?.length ? (
            <View style={{ marginTop: 10 }}>
              <SummaryItem
                title={`Transferências`}
                leftTitle="Valor"
                bordered
              />

              {data.transferencias.map((transferencia) => (
                <View style={{ flexDirection: "row" }} key={transferencia.id}>
                  <Text
                    style={{
                      fontWeight: "medium",
                      marginBottom: 6,
                      color: colors.black600,
                    }}
                  >
                    {moment(transferencia.data).format("DD/MM/YYYY")} - De{" "}
                    <Text
                      style={{
                        fontWeight: "medium",
                        textDecoration: "underline",
                      }}
                    >
                      {transferencia.contaOrigem}
                    </Text>{" "}
                    para{" "}
                    <Text
                      style={{
                        fontWeight: "medium",
                        textDecoration: "underline",
                      }}
                    >
                      {transferencia.contaDestino}
                    </Text>
                  </Text>
                  <Text style={{ marginLeft: "auto" }}>
                    {formatMoney(transferencia.valor, true)}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          <SummaryItem
            title={`Resultado (Receitas - Despesas)`}
            value={data.totais.resultado}
            bordered
            valueColor={data.totais.resultado < 0 ? colors.red500 : undefined}
          />
        </>
      ) : null}
    </PDFLayout>
  );
}

export default PDFRelatorioDemonstrativoReceitaDespesaAnual;
